import { Injectable } from '@angular/core';

@Injectable({
    providedIn: 'root',
})
export class IndexedDBService {
    private readonly dbName = 'EduScript';
    private readonly storeNames = [
        'Course',
        'Edit Course',
        'Lesson',
        'Edit Lesson',
        'Chapter',
        'Edit Chapter',
        'Section',
        'Edit Section',
    ];

    constructor() {
        this.initializeDB();
    }

    /**
     * Initializes the IndexedDB database and creates object stores if they do not exist.
     */
    private initializeDB(): void {
        const dbRequest = indexedDB.open(this.dbName, 1);

        dbRequest.onupgradeneeded = (event) => {
            const db = (event.target as IDBOpenDBRequest).result;

            this.storeNames.forEach((store) => {
                if (!db.objectStoreNames.contains(store)) {
                    db.createObjectStore(store, {
                        keyPath: 'id',
                        autoIncrement: true,
                    });
                }
            });
        };
    }

    /**
     * Adds or updates data in the specified object store.
     * @param storeName - The name of the object store.
     * @param id - The ID of the data to add or update.
     * @param data - The data to be added or updated.
     */
    addOrUpdateData<T>(
        storeName: string,
        id: number,
        data: T | undefined
    ): void {
        const dbRequest = indexedDB.open(this.dbName, 1);

        dbRequest.onsuccess = (event) => {
            const db = (event.target as IDBOpenDBRequest).result;

            if (!db.objectStoreNames.contains(storeName)) {
                db.close();
                this.deleteIndexedDB();

                const dbRecreateRequest = indexedDB.open(this.dbName, 1);

                dbRecreateRequest.onupgradeneeded = (event) => {
                    const newDb = (event.target as IDBOpenDBRequest).result;

                    this.storeNames.forEach((store) => {
                        if (!newDb.objectStoreNames.contains(store)) {
                            newDb.createObjectStore(store, {
                                keyPath: 'id',
                                autoIncrement: true,
                            });
                        }
                    });
                };

                dbRecreateRequest.onsuccess = () => {
                    dbRecreateRequest.result.close();
                    this.addOrUpdateData(storeName, id, data);
                };

                return;
            }

            const tx = db.transaction(storeName, 'readwrite');
            const store = tx.objectStore(storeName);
            const request = store.get(id);

            request.onsuccess = () => {
                if (request.result) {
                    store.put({ id, versions: data });
                } else {
                    store.add({ id, versions: data });
                }
            };
        };
    }

    /**
     * Deletes a specific version of a program from the specified object store.
     * @param storeName - The name of the object store.
     * @param programId - The ID of the program.
     * @param versionId - The ID of the version to delete.
     */
    deleteVersion(
        storeName: string,
        programId: number,
        versionId: number
    ): void {
        const dbRequest = indexedDB.open(this.dbName, 1);

        dbRequest.onsuccess = () => {
            const db = dbRequest.result;
            if (!db.objectStoreNames.contains(storeName)) {
                return;
            }

            const tx = db.transaction(storeName, 'readwrite');
            const store = tx.objectStore(storeName);

            const request = store.get(programId);

            request.onsuccess = () => {
                const programData = request.result;
                if (programData) {
                    // Filter out the version that needs to be removed
                    programData.versions = programData.versions.filter(
                        (version: any) => version.id !== versionId
                    );
                    if (programData.versions.length > 0) {
                        store.put(programData);
                    } else {
                        store.delete(programId);
                    }
                }
            };
        };
    }

    /**
     * Retrieves data by ID from the specified object store.
     * @param storeName - The name of the object store.
     * @param id - The ID of the data to retrieve.
     * @param callback - A callback function to handle the retrieved data.
     */
    getDataByKey<T>(
        storeName: string,
        id: number,
        callback: (result: T | undefined) => void
    ): void {
        const dbRequest = indexedDB.open(this.dbName, 1);

        dbRequest.onsuccess = () => {
            const db = dbRequest.result;
            if (!db.objectStoreNames.contains(storeName)) {
                callback(undefined);
                return;
            }

            const tx = db.transaction(storeName, 'readonly');
            const store = tx.objectStore(storeName);
            const request = store.get(id);

            request.onsuccess = () => {
                const result = request.result ? request.result.versions : [];
                callback(result);
            };
        };
    }

    /**
     * Retrieves all data from the specified object store.
     * @param storeName - The name of the object store.
     * @param callback - A callback function to handle the retrieved data.
     */
    getAllData<T>(storeName: string, callback: (result: T[]) => void): void {
        const dbRequest = indexedDB.open(this.dbName, 1);

        dbRequest.onsuccess = () => {
            const db = dbRequest.result;
            if (!db.objectStoreNames.contains(storeName)) {
                callback([]);
                return;
            }

            const tx = db.transaction(storeName, 'readonly');
            const store = tx.objectStore(storeName);
            const request = store.getAll();

            request.onsuccess = () => callback(request.result);
        };
    }

    /**
     * Deletes data by ID from the specified object store.
     * @param storeName - The name of the object store.
     * @param id - The ID of the data to delete.
     */
    deleteData(storeName: string, id: number): void {
        const dbRequest = indexedDB.open(this.dbName, 1);

        dbRequest.onsuccess = () => {
            const db = dbRequest.result;
            if (!db.objectStoreNames.contains(storeName)) {
                return;
            }

            const tx = db.transaction(storeName, 'readwrite');
            const store = tx.objectStore(storeName);
            store.delete(id);
        };
    }

    /**
     * Clears all data from the specified object store.
     * @param storeName - The name of the object store to clear.
     * @param callback - A callback function to indicate success or failure.
     */
    clearStore(storeName: string, callback: (success: boolean) => void): void {
        const dbRequest = indexedDB.open(this.dbName, 1);

        dbRequest.onsuccess = () => {
            const db = dbRequest.result;
            if (!db.objectStoreNames.contains(storeName)) {
                callback(false);
                return;
            }

            const tx = db.transaction(storeName, 'readwrite');
            const store = tx.objectStore(storeName);
            store.clear();

            tx.oncomplete = () => callback(true);
        };
    }

    /**
     * Deletes the entire IndexedDB database.
     */
    deleteIndexedDB(): void {
        indexedDB.deleteDatabase(this.dbName);
    }
}
